import React from "react";
import axios from "axios";
import {
  FormCheckboxField,
  FormToggleSwitch,
} from "../../components/form/checkbox";
import { FormDateField, FormTimeField } from "../../components/form/date";
import {
  FormDropDown,
  FormMultiSelectDropDown,
} from "../../components/form/dropdown";
import { FormFileUploadField } from "../../components/form/file";
import { FormImageField } from "../../components/form/image";
import FormJsonEditor from "../../components/form/json";
import { FormMultiFileUploadField } from "../../components/form/multi-file";
import { FormMultiImageField } from "../../components/form/multi-image";
import FormTextField from "../../components/form/text";
import { FormTextAreaField } from "../../components/form/text-area";
import { url } from "../../config";
import MainLayout from "../../layout";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { types, uiTypes } from "../../utils/types";
import {
  FormDropDownDynamic,
  FormMultiDropDownDynamic,
} from "../../components/form/dynamic-dropdown";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import successToast from "../../components/toast/successToast";
import errorToast from "../../components/toast/errorToast";
import FormTextArrayField from "../../components/form/text-array";
import { FormMultiImageVideoField } from "../../components/form/multi-image-video";
import { validatorTypes } from "../../utils/validatorTypes";
import { FormDocumentField } from "../../components/form/document-upload";
import { FormDocumentAltField } from "../../components/form/document-alt-upload";
import { FormVideoField } from "../../components/form/video-field";
import DynamicArrayJson from "../../components/form/dynamic-array-json-form";
import FormRichTextEditor from "../../components/form/form-editor";
import Editor from "../../components/editor";
import { ArrowLeft, PlusCircle, XCircle } from "lucide-react";

const Createpage = ({ model }) => {
  const navigate = useNavigate();
  const { tableName, columns, references } = model;

  const columnHeadings = () => {
    var array = [];
    for (var i = 0; i < columns.length; i++) {
      var column = columns[i];
      if (column.ui?.create === null || column.ui.create === true) {
        array.push(column.name);
      }
    }
    return array;
  };

  const headings = columnHeadings();

  const initialValues = columns.reduce((data, column) => {
    if (column.ui?.create == null || column.ui.create === true) {
      if (
        column.ui?.type === uiTypes?.SWITCH ||
        column.ui?.type === uiTypes?.CHECKBOX
      ) {
        data[column.name] = false;
      } else {
        data[column.name] = null;
      }
    }
    return data;
  }, {});

  const getValidationType = (validations = []) => {
    let schema = Yup.mixed();

    if (validations.includes(validatorTypes.string)) schema = Yup.string();
    else if (validations.includes(validatorTypes.email))
      schema = Yup.string().email("Invalid email");
    else if (validations.includes(validatorTypes.uuid))
      schema = Yup.string().uuid("Invalid UUID");
    else if (validations.includes(validatorTypes.number)) schema = Yup.number();
    else if (validations.includes(validatorTypes.bool)) schema = Yup.boolean();
    else if (validations.includes(validatorTypes.date)) schema = Yup.date();
    else if (validations.includes(validatorTypes.array)) schema = Yup.array();
    else if (validations.includes(validatorTypes.url))
      schema = Yup.string().url("Invalid URL");
    else if (validations.includes(validatorTypes.json)) schema = Yup.mixed();

    if (validations.includes(validatorTypes.required)) {
      schema = schema.required("This field is required");
    } else {
      schema = schema.notRequired();
    }

    return schema;
  };

  const validationSchema = Yup.object(
    columns.reduce((schema, column) => {
      if (column.ui?.create !== false) {
        const validations = column.ui?.validation?.length
          ? column.ui.validation
          : column.validation;

        schema[column.name] = getValidationType(validations);
      }
      return schema;
    }, {}),
  );

  const handleSubmit = (values) => {
    console.log("Submitted values:", values);
    axios
      .post(`${url}/model/${tableName}/create`, values)
      .then((response) => {
        successToast(response.message);
        console.log(response, "response");
        navigate(`/${tableName}/list`);
      })
      .catch((error) => {
        console.error("API Error:", error);
        errorToast(error?.response?.data?.message);
      });
  };

  const enumToOptions = (enumValues = []) =>
    enumValues.map((val) => ({
      label: val.charAt(0).toUpperCase() + val.slice(1),
      value: val,
    }));

  return (
    <MainLayout>
      <section className="h-full overflow-auto px-1 sm:px-3 md:px-6  bg-slate-50/40">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <div className="sticky top-0 z-30 sm:-mx-6 px-4 sm:px-6 mb-6 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-2xs flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 py-3 transition-all">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={() => navigate(-1)}
                    type="button"
                    className="p-2 shrink-0 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all border border-slate-200/60 active:scale-95"
                    title="Go Back"
                  >
                    <ArrowLeft className="w-4 h-4 text-slate-600" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base sm:text-lg font-bold text-slate-900 capitalize tracking-tight truncate">
                      Create {formatUnderscoreToSpace(tableName)}
                    </h2>
                    <span className="text-[11px] font-mono text-slate-400 font-medium block truncate">
                      New Record Entry
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => navigate(`/${tableName}/list`)}
                    type="button"
                    className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 text-xs md:text-sm font-semibold px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200/80 rounded-xl transition-all active:scale-95"
                  >
                    <XCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    // disabled={isSubmitting}
                    className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 text-xs md:text-sm font-semibold px-4 py-2 bg-primary hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl shadow-xs transition-all active:scale-95"
                  >
                    <PlusCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>Create</span>
                  </button>
                </div>
              </div>

              <div className="max-w-7xl mx-auto mb-12">
                <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs overflow-hidden p-3 sm:p-8 min-h-[50vh]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                    {headings.map((heading) => {
                      const column = columns.find(
                        (value) => value.name === heading,
                      );

                      if (column?.ui?.display === false) {
                        return null;
                      }

                      const canCreate =
                        column.ui?.create === null ||
                        column?.ui?.create === true;

                      if (canCreate && column?.ui?.type === uiTypes?.SWITCH) {
                        return (
                          <div
                            key={heading}
                            className="col-span-1 flex items-center"
                          >
                            <FormToggleSwitch
                              name={heading}
                              label={heading}
                              value={values?.[heading]}
                            />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes?.NUMBER) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormTextField
                              name={heading}
                              label={heading}
                              placeholder={`Enter ${heading}`}
                              type="number"
                            />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.ui?.type === uiTypes?.TEXT_ARRAY
                      ) {
                        return (
                          <div
                            key={heading}
                            className="col-span-1 flex items-center"
                          >
                            <FormTextArrayField
                              name={heading}
                              label={heading}
                            />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes?.CHECKBOX) {
                        return (
                          <div
                            key={heading}
                            className="col-span-1 flex items-center"
                          >
                            <FormCheckboxField name={heading} label={heading} />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes?.TIME) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormTimeField name={heading} label={heading} />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes?.DATE) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormDateField name={heading} label={heading} />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.ui?.type === uiTypes?.DATE_TIME
                      ) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormDateField
                              name={heading}
                              label={heading}
                              type="datetime-local"
                            />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.ui?.type === uiTypes?.TEXT_AREA
                      ) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormTextAreaField
                              name={heading}
                              label={heading}
                              placeholder={`Enter ${heading}`}
                            />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes?.DROPDOWN) {
                        if (!column?.enumValues) {
                          var reference = references?.find(
                            (value) => value.foreignKey === heading,
                          );
                          return (
                            <div key={heading} className="col-span-1">
                              <FormDropDownDynamic
                                name={heading}
                                label={heading}
                                reference={reference}
                                placeholder="Select"
                                menuPlacement={
                                  column?.ui?.menuTopPlacement
                                    ? "top"
                                    : "bottom"
                                }
                              />
                            </div>
                          );
                        }

                        return (
                          <div key={heading} className="col-span-1">
                            <FormDropDown
                              name={heading}
                              label={heading}
                              options={enumToOptions(column?.enumValues)}
                              value={enumToOptions(column?.enumValues)?.find(
                                (item) => item?.value === values?.[heading],
                              )}
                              menuPlacement={
                                column?.ui?.menuTopPlacement ? "top" : "bottom"
                              }
                            />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.ui?.type === uiTypes?.DROPDOWN_MULTISELECT
                      ) {
                        if (!column?.enumValues) {
                          var reference = references?.find(
                            (value) => value.foreignKey === heading,
                          );
                          return (
                            <div key={heading} className="col-span-1">
                              <FormMultiDropDownDynamic
                                name={heading}
                                label={heading}
                                reference={reference}
                                placeholder="Select"
                                menuPlacement={
                                  column?.ui?.menuTopPlacement
                                    ? "top"
                                    : "bottom"
                                }
                              />
                            </div>
                          );
                        }

                        return (
                          <div key={heading} className="col-span-1">
                            <FormMultiSelectDropDown
                              name={heading}
                              label={heading}
                              options={enumToOptions(column?.enumValues)}
                              value={enumToOptions(column?.enumValues)?.find(
                                (item) => item?.value === values?.[heading],
                              )}
                              menuPlacement={
                                column?.ui?.menuTopPlacement ? "top" : "bottom"
                              }
                            />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes.IMAGE) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormImageField name={heading} label={heading} />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes.DOCUMENT) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormDocumentField name={heading} label={heading} />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.ui?.type === uiTypes.DOCUMENT_ALT
                      ) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormDocumentAltField
                              name={heading}
                              label={heading}
                            />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes.VIDEO) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormVideoField name={heading} label={heading} />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.isArray &&
                        column?.ui?.type === uiTypes.MULTI_IMAGE
                      ) {
                        return (
                          <div key={heading} className="col-span-full">
                            <FormMultiImageField
                              name={heading}
                              label={heading}
                            />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.isArray &&
                        column?.ui?.type === uiTypes.MULTI_IMAGE_VIDEO
                      ) {
                        return (
                          <div key={heading} className="col-span-full">
                            <FormMultiImageVideoField
                              name={heading}
                              label={heading}
                            />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes.FILE) {
                        return (
                          <div key={heading} className="col-span-1">
                            <FormFileUploadField
                              name={heading}
                              label={heading}
                              accept=".pdf,.doc,.docx"
                            />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.isArray &&
                        column?.ui?.type === uiTypes.MULTI_FILE
                      ) {
                        return (
                          <div key={heading} className="col-span-full">
                            <FormMultiFileUploadField
                              name={heading}
                              label={heading}
                              accept=".pdf,.doc,.docx,.jpg,.png"
                            />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes?.JSON) {
                        return (
                          <div key={heading} className="col-span-full">
                            <FormJsonEditor
                              name={heading}
                              label={heading}
                              value={column?.defaultValue}
                            />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.ui?.type === uiTypes?.DYNAMICJSON
                      ) {
                        return (
                          <div key={heading} className="col-span-full">
                            <DynamicArrayJson
                              name={heading}
                              label={heading}
                              value={column?.defaultValue}
                              dynamicArrayfeilds={
                                column?.ui?.dynamicArrayfeilds
                              }
                            />
                          </div>
                        );
                      }

                      if (
                        canCreate &&
                        column?.ui?.type === uiTypes?.RICH_TEXT_EDITOR
                      ) {
                        return (
                          <div key={heading} className="col-span-full">
                            <FormRichTextEditor
                              name={heading}
                              label={heading}
                            />
                          </div>
                        );
                      }

                      if (canCreate && column?.ui?.type === uiTypes?.EDITOR) {
                        return (
                          <div key={heading} className="col-span-full">
                            <Editor name={heading} label={heading} />
                          </div>
                        );
                      }

                      return (
                        canCreate && (
                          <div key={heading} className="col-span-1">
                            <FormTextField
                              label={heading}
                              name={heading}
                              placeholder={`Enter ${heading}`}
                            />
                          </div>
                        )
                      );
                    })}
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </section>
    </MainLayout>
  );
};

export default Createpage;
